import {
  App,
  Col,
  Row,
  TableColumnsType,
  Tooltip,
  Button as ButtonAnt,
  Switch
} from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Table } from '@/components/tables/table';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  deleteBanner,
  updateBannerSlider,
  resetMessages,
  watchBannerLoading,
  watchBannerMessage,
  watchBannerPagination,
  watchListBanner
} from '@/redux/slices/banner.slice';
import { useEffect } from 'react';
import { setMessages } from '@/redux/slices/app.slice';
import IMAGE_DEFAULT from '@/assets/images/image_default.png';
import { useQuery } from '@/hooks/query.hook';
import { PageURLs } from '@/utils/navigate';
import { PermissionMode } from '@/enums/mode.enum';
import { CustomMessage } from '../message';
import { formatDateToDDMMYYYY, tableScrollY } from '@/utils/common';
import usePermission from '@/hooks/permission.hook';
import { useLocation } from 'react-router-dom';

type PageChangeParams = {
  page?: number;
  pageSize?: number;
  filter?: FilterBanner;
};

type BannerSliderTableProps = {
  handleSearch: (values: FilterBanner) => void;
  handlePageChange: ({ page, pageSize, filter }: PageChangeParams) => void;
  filter: FilterBanner;
};

export const BannerSliderTable = ({
  handleSearch,
  handlePageChange,
  filter
}: BannerSliderTableProps) => {
  const dispatch = useAppDispatch();
  const { modal } = App.useApp();
  const listBanner = useAppSelector(watchListBanner);
  const messageInfo = useAppSelector(watchBannerMessage);
  const pagination = useAppSelector(watchBannerPagination);
  const loading = useAppSelector(watchBannerLoading);
  const { push } = useQuery();
  const { updatable, deletable } = usePermission();
  const location = useLocation();

  const sortedBanner = [...listBanner].sort(
    (a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0)
  );

  const handleReset = () => {
    handlePageChange({filter});
  };

  const handleDeleteBanner = async (bannerId: number) => {
    await dispatch(deleteBanner(bannerId));
    const remainingRecords = listBanner.length - 1;
    if (remainingRecords === 0 && pagination.curPage > 1) {
      handlePageChange({
        page: pagination.curPage - 1,
        pageSize: pagination.limitPage,
        filter
      });
    } else {
      handlePageChange({
        page: pagination.curPage,
        pageSize: pagination.limitPage,
        filter
      });
    }
  };

  const handleToggleActive = async (banner: any, checked: boolean) => {
    const body = {
      id: banner.id,
      is_active: checked ? 'TRUE' : 'FALSE',
      id_platform: banner.option.id,
      redirect_url: banner.redirectUrl,
      name: banner.name
    };
    await dispatch(updateBannerSlider(body));
  };

  const confirmDeleteAdmin = (bannerId: number) => {
    modal.confirm({
      title: 'Bạn có chắc chắn xoá Banner này không?',
      content: <em>Sau khi xoá sẽ không thể hoàn tác</em>,
      okText: 'Xác nhận',
      width: 450,
      cancelText: 'Hủy',
      onOk: () => handleDeleteBanner(bannerId)
    });
  };

  const handleEditBanner = (bannerId: string) => {
    push(
      `${PageURLs.ofHomeBannerSlider()}?mode=${PermissionMode.EDIT}&bannerId=${bannerId}`
    );
  };

  const columns: TableColumnsType<Banner> = [
    {
      title: 'STT',
      render: (_, __, index) => index + 1
    },
    {
      title: 'Hình banner',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      render: (record) => (
        <img
          src={record ? `${record}` : IMAGE_DEFAULT}
          alt='Banner-slider'
          style={{ width: '50px', height: '32px', objectFit: 'cover' }}
        />
      )
    },
    {
      title: 'Tên banner',
      dataIndex: 'name',
      key: 'name',
      width: 200
    },
    {
      title: 'Hiển thị',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (_, record) => (
        <Switch
          disabled={!updatable}
          defaultChecked={record.isActive ? true : false}
          onChange={(checked) => handleToggleActive(record, checked)}
        />
      )
    },
    {
      title: 'Nền tảng',
      dataIndex: ['option', 'name'],
      render: (__, record) => {
        let messageType: MessageType;
        switch (record?.option?.name) {
          case 'Website cho pc':
            messageType = 'website';
            break;
          case 'Website cho Mobile':
            messageType = 'website-mobile';
            break;
          case 'App':
            messageType = 'app';
            break;
          default:
            messageType = '';
        }
        return (
          record.option && (
            <CustomMessage hasBackground type={messageType}>
              {record?.option?.name}
            </CustomMessage>
          )
        );
      }
    },
    {
      title: 'URL',
      dataIndex: 'redirectUrl',
      key: 'redirectUrl',
      width: 200
    },
    {
      title: 'Người tạo',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 200
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdDate',
      key: 'createdDate',
      width: 200,
      render: (record) => (record ? formatDateToDDMMYYYY(record) : '')
    },
    {
      title: 'Người cập nhật',
      dataIndex: 'modifiedBy',
      key: 'modifiedBy',
      width: 200
    },
    {
      title: 'Ngày cập nhật',
      dataIndex: 'modifiedDate',
      key: 'modifiedDate',
      width: 200,
      render: (record) => (record ? formatDateToDDMMYYYY(record) : '')
    },
    {
      title: 'Thao tác',
      key: 'action',
      fixed: 'right',
      width: 104,
      render: (record) => (
        <Row gutter={[8, 10]}>
          {updatable && (
            <Col>
              <Tooltip title='Cập nhật'>
                <ButtonAnt
                  onClick={() => handleEditBanner(record.id)}
                  type='primary'
                  icon={<EditOutlined />}
                />
              </Tooltip>
            </Col>
          )}
          {deletable && (
            <Col>
              <Tooltip title='Xoá'>
                <ButtonAnt
                  onClick={() => confirmDeleteAdmin(record.id)}
                  type='primary'
                  danger
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Col>
          )}
        </Row>
      )
    }
  ];

  useEffect(() => {
    handlePageChange({});
  }, [handlePageChange, location.pathname]);

  useEffect(() => {
    if (messageInfo) {
      dispatch(
        setMessages([
          {
            type: messageInfo.type,
            message: messageInfo.message,
            description: messageInfo.description
          }
        ])
      );
      dispatch(resetMessages());
    }
  }, [dispatch, messageInfo]);
  return (
    <Table<Banner>
      rowKey={(record) => record.id}
      columns={columns}
      dataSource={sortedBanner}
      placeholder='Nhập tên banner để tìm kiếm'
      loading={loading}
      totalResult={pagination.totalRows}
      scrollY={tableScrollY(pagination.limitPage)}
      pagination={{
        showQuickJumper: true,
        current: pagination.curPage,
        pageSize: pagination.limitPage,
        total: pagination.totalRows,
        onChange: (page, pageSize) => {
          handlePageChange({ page, pageSize });
        }
      }}
      onFilter={handleSearch}
      onReset={handleReset}
    />
  );
};
